import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import {
  ArrowLeftOutlined,
  CarOutlined,
  DeliveredProcedureOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  HomeOutlined,
  SearchOutlined,
  SendOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getAllNonServiceablePincodes,
  getHubMapListing,
} from "../../services/api/pincodeServiceabilityApi";
import {
  askPincodeMapAssistant,
} from "../../services/api/pincodeMapAssistantApi";
import {
  PINCODE_MAP_FILTER_CARD_KEYS,
  PINCODE_MAP_FILTER_PANEL_TEXT,
  PINCODE_MAP_MESSAGES,
  PINCODE_MAP_STYLE_TOKENS,
  REQUEST_STATUSES,
} from "../../constants/pincodeMapConstants";
import {
  getCourierLaneFollowUp,
  getHubMappingFollowUp,
  getServiceabilityFollowUp,
  getShowOnMapLabel,
  PINCODE_MAP_ASSISTANT_FOLLOWUP,
  PINCODE_MAP_ASSISTANT_LIMITS,
  PINCODE_MAP_ASSISTANT_SUGGESTIONS,
  PINCODE_MAP_ASSISTANT_TEXT,
} from "../../constants/pincodeMapAssistantConstants";

const T = {
  bg: "#fafaf8",
  white: "#ffffff",
  border: "#ecebe6",
  text: "#171717",
  muted: "#6b7280",
  soft: "#9ca3af",
  blue: "#2563eb",
  green: "#059669",
  orange: "#ea580c",
  purple: "#7c3aed",
  red: "#dc2626",
  teal: "#0d9488",
};

const INDIA_CENTER = [22.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const PINCODE_FOCUS_ZOOM = 10;
const EXACT_PINCODE_PATTERN = /^\d{6}$/;
const POPUP_OPEN_MAX_RETRIES = 25;
const POPUP_OPEN_RETRY_DELAY_MS = 120;
const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CATEGORY_KEYS = ["courier", "ecomm", "mcn"];
const NUMERIC_QUERY_PATTERN = /^\d+$/;
const PIN_CACHE = {};
const { FULFILLED } = REQUEST_STATUSES;

const CATEGORY_CONFIG = {
  courier: {
    key: "courier",
    label: "Courier",
    mapLabel: "Courier",
    filterLabel: "Courier",
    legendLabel: "Courier",
    statLabel: "Courier",
    tone: "courier",
    Icon: CarOutlined,
  },
  ecomm: {
    key: "ecomm",
    label: "E-commerce",
    mapLabel: "E-com",
    filterLabel: "E-commerce",
    legendLabel: "E-commerce",
    statLabel: "E-commerce",
    tone: "ecomm",
    Icon: ShoppingOutlined,
  },
  mcn: {
    key: "mcn",
    label: "Courier (MCN / 3PL)",
    mapLabel: "MCN",
    filterLabel: "Courier (MCN / 3PL)",
    legendLabel: "Courier (MCN / 3PL)",
    statLabel: "Courier (MCN / 3PL)",
    tone: "mcn",
    Icon: DeliveredProcedureOutlined,
  },
  nonsvc: {
    key: "nonsvc",
    label: "Non Serviceable",
    mapLabel: "Non Svc",
    filterLabel: "Non Serviceable",
    legendLabel: "Non Serviceable",
    statLabel: "Non Serviceable",
    tone: "nonsvc",
    Icon: WarningOutlined,
  },
  hubs: {
    key: "hubs",
    label: "Active Hubs",
    mapLabel: "Hubs",
    filterLabel: "Active Hubs",
    legendLabel: "Hub",
    statLabel: "Active Hubs",
    tone: "hub",
    Icon: HomeOutlined,
  },
};

const toneColor = {
  courier: T.blue,
  ecomm: T.green,
  mcn: T.orange,
  multi: T.purple,
  nonsvc: T.red,
  hub: T.teal,
};

const DEFAULT_FILTERS = {
  courier: true,
  ecomm: true,
  mcn: true,
  nonsvc: false,
  hubs: true,
};

const RESULT_LIMIT = 200;
const {
  chatHistoryLimit: CHAT_HISTORY_LIMIT,
  maxConversationTurns: MAX_ASSISTANT_CONVERSATION_TURNS,
} = PINCODE_MAP_ASSISTANT_LIMITS;
const ASSISTANT_SUGGESTIONS = PINCODE_MAP_ASSISTANT_SUGGESTIONS;

const {
  fallbackPrimaryPincode: ASSISTANT_FOLLOWUP_PRIMARY_PINCODE,
  fallbackSecondaryPincode: ASSISTANT_FOLLOWUP_SECONDARY_PINCODE,
  fallbackAlternatePincode: ASSISTANT_FOLLOWUP_ALTERNATE_PINCODE,
} = PINCODE_MAP_ASSISTANT_FOLLOWUP;

const popupCardStyle = {
  background: T.white,
  borderRadius: 12,
  border: `1px solid ${T.border}`,
  padding: 14,
  minWidth: 210,
};

const popupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
};

const popupMetaStyle = {
  fontSize: 11,
  color: T.soft,
};

const popupMonoStyle = {
  fontSize: 11,
  color: T.soft,
  fontFamily: "JetBrains Mono, monospace",
};

const mapControlsWrapStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  zIndex: 800,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const mapControlStyle = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.white,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  cursor: "pointer",
  color: T.muted,
};

const num = (value) => Number(value || 0).toLocaleString("en-IN");
const txt = (value) => (value == null ? "" : String(value).trim());
const time = (value) =>
  value?.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  }) || "";

const n = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const b = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE";

const pickBool = (item, keys) => keys.some((key) => b(item?.[key]));
const title = (value) =>
  txt(value)
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
const contains = (value, query) => txt(value).toLowerCase().includes(query);
const normalizeSearchToken = (value) => txt(value).toLowerCase();
const decodeParam = (value) => {
  if (!value) return "";

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const percentage = (value, total) =>
  `${total ? Math.round((value / total) * 100) : 0}% of total`;

const mapPincode = (item, index) => {
  const courier = pickBool(item, ["courier", "isCourier", "is_courier"]);
  const ecomm = pickBool(item, [
    "ecomm",
    "ecommerce",
    "e_commerce",
    "isEcomm",
  ]);
  const mcn = pickBool(item, ["mcn", "isMcn", "is_mcn", "threePl"]);
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: `${txt(item?.pincode || item?.pinCode)}-${index}`,
    pincode: txt(item?.pincode ?? item?.pinCode ?? item?.pin),
    city: title(item?.city ?? item?.cityName ?? item?.district),
    state: title(item?.state ?? item?.stateName),
    zone: txt(item?.zone ?? item?.zoneName).toUpperCase(),
    courier,
    ecomm,
    mcn,
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
    isNonServiceable: !courier && !ecomm && !mcn,
  };
};

const mapHub = (item, index) => {
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: String(item?.premiseId ?? item?.id ?? `hub-${index}`),
    premiseName: txt(item?.premiseName ?? item?.name),
    city: title(item?.city),
    state: title(item?.state),
    zone: txt(item?.zone).toUpperCase(),
    pincode: txt(item?.pincode),
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
  };
};

const mergePincodeDatasets = (serviceablePins, nonServiceablePins) => {
  const nonServiceableOnly = nonServiceablePins.filter((item) => item.isNonServiceable);
  const mergedByPincode = new Map();

  [...serviceablePins, ...nonServiceableOnly].forEach((item) => {
    if (!item.pincode) return;

    const existing = mergedByPincode.get(item.pincode);
    if (!existing) {
      mergedByPincode.set(item.pincode, item);
      return;
    }

    const courier = existing.courier || item.courier;
    const ecomm = existing.ecomm || item.ecomm;
    const mcn = existing.mcn || item.mcn;
    const lat = existing.lat ?? item.lat;
    const lng = existing.lng ?? item.lng;

    mergedByPincode.set(item.pincode, {
      ...existing,
      courier,
      ecomm,
      mcn,
      lat,
      lng,
      city: existing.city || item.city,
      state: existing.state || item.state,
      zone: existing.zone || item.zone,
      hasCoordinates: lat !== null && lng !== null,
      isNonServiceable: !courier && !ecomm && !mcn,
    });
  });

  return Array.from(mergedByPincode.values());
};

const getPincodeTone = (item) => {
  const activeLines = CATEGORY_KEYS.filter((key) => item[key]).length;
  if (item.isNonServiceable) return "nonsvc";
  if (activeLines > 1) return "multi";
  if (item.courier) return "courier";
  if (item.ecomm) return "ecomm";
  return "mcn";
};

const getResultTagTones = (item) => {
  if (item.isNonServiceable) return [];

  const tones = CATEGORY_KEYS.filter((key) => item[key]);
  return getPincodeTone(item) === "multi" ? [...tones, "multi"] : tones;
};

const matchesQuery = (values, query) =>
  !query || values.some((value) => contains(value, query));

const matchesFilters = (item, filters) => {
  if (item.isNonServiceable) return filters.nonsvc;
  return CATEGORY_KEYS.some((key) => filters[key] && item[key]);
};

const shouldShowHubs = (filters) => filters.hubs;

const buildPincodeStats = (items) =>
  items.reduce(
    (acc, item) => ({
      serviceable: acc.serviceable + (item.isNonServiceable ? 0 : 1),
      courier: acc.courier + (item.courier ? 1 : 0),
      ecomm: acc.ecomm + (item.ecomm ? 1 : 0),
      mcn: acc.mcn + (item.mcn ? 1 : 0),
      nonsvc: acc.nonsvc + (item.isNonServiceable ? 1 : 0),
    }),
    {
      serviceable: 0,
      courier: 0,
      ecomm: 0,
      mcn: 0,
      nonsvc: 0,
    }
  );

const buildStats = (pincodes, hubs) => ({
  ...buildPincodeStats(pincodes),
  hubs: hubs.length,
  zones: new Set(hubs.map((item) => item.zone).filter(Boolean)).size,
});

const buildMeta = (pincodes, mappedPincodes, hubs, mappedHubs) => ({
  pincodeTotal: pincodes.length,
  pincodeMapped: mappedPincodes.length,
  pincodeMissing: pincodes.length - mappedPincodes.length,
  hubTotal: hubs.length,
  hubMapped: mappedHubs.length,
});

const buildStatCards = (stats) => [
  {
    key: "all",
    label: "Total Serviceable",
    value: stats.serviceable,
    sub: "across all lines",
    tone: "multi",
    Icon: EnvironmentOutlined,
  },
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.statLabel,
    value: stats.courier,
    sub: percentage(stats.courier, stats.serviceable),
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.statLabel,
    value: stats.ecomm,
    sub: percentage(stats.ecomm, stats.serviceable),
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.statLabel,
    value: stats.mcn,
    sub: percentage(stats.mcn, stats.serviceable),
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.statLabel,
    value: stats.nonsvc,
    sub: "no coverage",
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.statLabel,
    value: stats.hubs,
    sub: `${num(stats.zones)} active zones`,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const legendItems = [
  CATEGORY_CONFIG.courier,
  CATEGORY_CONFIG.ecomm,
  CATEGORY_CONFIG.mcn,
  {
    key: "multi",
    legendLabel: "Multiple",
    tone: "multi",
  },
  CATEGORY_CONFIG.nonsvc,
  CATEGORY_CONFIG.hubs,
];

const buildMapInfoItems = (stats) => [
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.mapLabel,
    value: stats.courier,
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.mapLabel,
    value: stats.ecomm,
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.mapLabel,
    value: stats.mcn,
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.mapLabel,
    value: stats.nonsvc,
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.mapLabel,
    value: stats.hubs,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const HUB_LABEL_CLEANUP_PATTERN = /[^a-z0-9]+/g;
const PINCODE_IN_TEXT_PATTERN = /\b\d{6}\b/g;
const HUB_IN_ANSWER_PATTERN = /mapped to hub:\s*([^\n.]+)/i;

const normalizeHubLabel = (value) =>
  normalizeSearchToken(value)
    .replace(HUB_LABEL_CLEANUP_PATTERN, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractPincodesFromText = (value) =>
  toUniqueTexts(txt(value).match(PINCODE_IN_TEXT_PATTERN) || []);

const extractHubFromAnswer = (value) => {
  const answer = txt(value);
  if (!answer) return "";
  const match = answer.match(HUB_IN_ANSWER_PATTERN);
  return txt(match?.[1]);
};

const resolveMapPointsFromEntities = (
  entities = {},
  pincodeMap = new Map(),
  hubs = [],
  options = {}
) => {
  const points = [];
  const seen = new Set();
  const resolvedPincodes = Array.isArray(entities?.pincodes) ? entities.pincodes : [];
  const resolvedHubs = Array.isArray(entities?.hubs) ? entities.hubs : [];
  const answerText = txt(options?.answerText);
  const questionText = txt(options?.questionText);
  const citations = Array.isArray(options?.citations) ? options.citations : [];

  const citationEntityIds = citations.flatMap((citation) =>
    Array.isArray(citation?.entity_ids) ? citation.entity_ids : []
  );
  const pincodeCandidates = toUniqueTexts([
    ...resolvedPincodes,
    ...citationEntityIds.filter((value) => EXACT_PINCODE_PATTERN.test(txt(value))),
    ...extractPincodesFromText(answerText),
    ...extractPincodesFromText(questionText),
  ]);

  pincodeCandidates.forEach((value) => {
    const key = txt(value);
    if (!key) return;
    const record = pincodeMap.get(key);
    if (!record || !record.hasCoordinates) return;
    const dedupeKey = `pin-${record.pincode}`;
    if (seen.has(dedupeKey)) return;

    seen.add(dedupeKey);
    points.push({
      id: dedupeKey,
      lat: record.lat,
      lng: record.lng,
      label: `Pincode ${record.pincode}`,
      pointType: "pincode",
      popupItem: record,
    });
  });

  const answerHub = extractHubFromAnswer(answerText);
  const hubCandidates = toUniqueTexts([...resolvedHubs, answerHub]);

  hubCandidates.forEach((value) => {
    const targetLabel = normalizeHubLabel(value);
    if (!targetLabel) return;

    const matchedHub = hubs.find((hub) => {
      const hubName = normalizeHubLabel(hub.premiseName);
      return (
        hubName === targetLabel ||
        hubName.includes(targetLabel) ||
        targetLabel.includes(hubName)
      );
    });
    if (!matchedHub || !matchedHub.hasCoordinates) return;

    const dedupeKey = `hub-${matchedHub.id}`;
    if (seen.has(dedupeKey)) return;

    seen.add(dedupeKey);
    points.push({
      id: dedupeKey,
      lat: matchedHub.lat,
      lng: matchedHub.lng,
      label: matchedHub.premiseName || "Mapped Hub",
      pointType: "hub",
      popupItem: matchedHub,
    });
  });

  return points;
};

const buildAssistantConversation = (messages) =>
  messages
    .filter((item) => item?.role === "user" || item?.role === "assistant")
    .map((item) => ({
      role: item.role,
      content: txt(item.content),
    }))
    .filter((item) => item.content)
    .slice(-MAX_ASSISTANT_CONVERSATION_TURNS);

const ASSISTANT_INTERNAL_NOTE_PATTERN =
  /^\s*(data\s*source|sources?|api|endpoint|endpoints|citation|citations|debug)\s*[:-]/i;

const sanitizeAssistantAnswer = (value) => {
  const raw = txt(value);
  if (!raw) return "";

  const cleaned = raw
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => !ASSISTANT_INTERNAL_NOTE_PATTERN.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned || raw;
};

const ASSISTANT_GENERIC_FOLLOW_UP_PATTERNS = {
  anotherPincode: /^check another pincode[.?!]*$/i,
  courierLane: /^check (a )?from[- ]to courier lane[.?!]*$/i,
  hubMapping: /^ask for hub mapping[.?!]*$/i,
};

const resolveFallbackPincode = (
  value,
  fallback = ASSISTANT_FOLLOWUP_PRIMARY_PINCODE
) => {
  const candidate = txt(value);
  return EXACT_PINCODE_PATTERN.test(candidate) ? candidate : fallback;
};

const toUniqueTexts = (values = []) =>
  Array.from(
    new Set(
      values
        .map((value) => txt(value))
        .filter(Boolean)
    )
  );

const buildActionableFollowUps = ({
  followUps = [],
  resolvedEntities = {},
  selectedPincode,
  currentQuestion,
}) => {
  const resolvedPincodes = toUniqueTexts(
    Array.isArray(resolvedEntities?.pincodes) ? resolvedEntities.pincodes : []
  ).filter((pincode) => EXACT_PINCODE_PATTERN.test(pincode));

  const primaryPincode = resolveFallbackPincode(
    resolvedPincodes[0] || selectedPincode,
    ASSISTANT_FOLLOWUP_PRIMARY_PINCODE
  );
  const secondaryPincode = resolveFallbackPincode(
    resolvedPincodes[1],
    primaryPincode === ASSISTANT_FOLLOWUP_SECONDARY_PINCODE
      ? ASSISTANT_FOLLOWUP_ALTERNATE_PINCODE
      : ASSISTANT_FOLLOWUP_SECONDARY_PINCODE
  );
  const alternatePincode =
    primaryPincode === ASSISTANT_FOLLOWUP_PRIMARY_PINCODE
      ? ASSISTANT_FOLLOWUP_ALTERNATE_PINCODE
      : ASSISTANT_FOLLOWUP_PRIMARY_PINCODE;

  const transformedFollowUps = toUniqueTexts(followUps).map((followUp) => {
    if (ASSISTANT_GENERIC_FOLLOW_UP_PATTERNS.anotherPincode.test(followUp)) {
      return getServiceabilityFollowUp(alternatePincode);
    }
    if (ASSISTANT_GENERIC_FOLLOW_UP_PATTERNS.courierLane.test(followUp)) {
      return getCourierLaneFollowUp(primaryPincode, secondaryPincode);
    }
    if (ASSISTANT_GENERIC_FOLLOW_UP_PATTERNS.hubMapping.test(followUp)) {
      return getHubMappingFollowUp(primaryPincode);
    }
    return followUp;
  });

  const normalizedCurrentQuestion = normalizeSearchToken(currentQuestion);
  const actionable = toUniqueTexts(transformedFollowUps).filter(
    (followUp) => normalizeSearchToken(followUp) !== normalizedCurrentQuestion
  );

  if (actionable.length) {
    return actionable.slice(0, 3);
  }

  return [
    getServiceabilityFollowUp(alternatePincode),
    getHubMappingFollowUp(primaryPincode),
    getCourierLaneFollowUp(primaryPincode, secondaryPincode),
  ];
};

const chipStyle = (tone, active) => ({
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  background: active ? `${toneColor[tone]}15` : "#f5f5f5",
  color: active ? toneColor[tone] : T.soft,
  border: `1px solid ${active ? `${toneColor[tone]}33` : T.border}`,
});

const pinIcon = (tone) => {
  if (!PIN_CACHE[tone]) {
    PIN_CACHE[tone] = L.divIcon({
      className: "",
      html: `<div class="pinmap-pin ${tone}"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -10],
    });
  }

  return PIN_CACHE[tone];
};

const hubIcon = L.divIcon({
  className: "",
  html: `
    <div class="pinmap-hub">
      <div class="pinmap-hub-pulse"></div>
      <div class="pinmap-hub-core">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0d9488"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35z"></path>
          <path d="M6 18h12"></path>
          <path d="M6 14h12"></path>
        </svg>
      </div>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -18],
});

const assistantFocusIcon = L.divIcon({
  className: "",
  html: "<div class=\"pinmap-assistant-focus\"></div>",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const allNonServiceable = cluster
    .getAllChildMarkers()
    .every((marker) => marker.options.markerTone === "nonsvc");
  const size = count < 10 ? 34 : count < 100 ? 42 : 50;

  return L.divIcon({
    className: "",
    html: `<div class="pinmap-cluster ${
      allNonServiceable ? "nonsvc" : ""
    }" style="width:${size}px;height:${size}px">${
      count > 999 ? "999+" : count
    }</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const StatCard = ({ item, active, onSelect }) => {
  const { key, label, value, sub, tone, Icon } = item;

  return (
    <button
      type="button"
      className={`pinmap-stat-card ${active ? "pinmap-active" : ""}`}
      onClick={() => onSelect(key)}
      style={{ "--pinmap-tone": toneColor[tone] }}
    >
      <div className="pinmap-stat-label">
        <span className="pinmap-stat-icon">
          <Icon />
        </span>
        {label}
      </div>
      <div className="pinmap-stat-value">{num(value)}</div>
      <div className="pinmap-stat-sub">{sub}</div>
      <div className="pinmap-stat-bar"></div>
    </button>
  );
};

const Legend = () => (
  <div className="pinmap-legend-grid">
    {legendItems.map((item) => {
      const Icon = item.Icon;

      return (
        <div key={item.key} className="pinmap-legend-item">
          {Icon ? (
            <Icon style={{ color: toneColor[item.tone] }} />
          ) : (
            <span
              className="pinmap-legend-dot"
              style={{ background: toneColor[item.tone] }}
            ></span>
          )}
          {item.legendLabel}
        </div>
      );
    })}
  </div>
);

const ResultRow = ({ item, onFocus }) => {
  const isHub = item.resultType === "hub";

  return (
    <button
      type="button"
      className="pinmap-result-row"
      onClick={() => onFocus(item, isHub ? "hub" : "pincode")}
    >
      <div className="pinmap-result-pin">{isHub ? item.premiseName : item.pincode}</div>
      <div className="pinmap-result-city">
        {[item.city, item.state].filter(Boolean).join(", ") ||
          (isHub ? "Active hub" : "Mapped pincode")}
      </div>
      {isHub ? (
        <span className="pinmap-result-hub">HUB</span>
      ) : item.isNonServiceable ? (
        <span className="pinmap-result-nonsvc">NO SVC</span>
      ) : (
        <div className="pinmap-result-tags">
          {getResultTagTones(item).map((tone) => (
            <span
              key={`${item.id}-${tone}`}
              className="pinmap-result-tag"
              style={{ background: toneColor[tone] }}
            ></span>
          ))}
        </div>
      )}
    </button>
  );
};

const PincodePopup = ({ item }) => {
  const tone = getPincodeTone(item);

  return (
    <div style={popupCardStyle}>
      <div style={popupHeaderStyle}>
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            background: toneColor[tone],
          }}
        ></div>
        <div>
          <div style={{ fontWeight: 700, color: T.text }}>{item.pincode}</div>
          <div style={popupMetaStyle}>
            {[item.city, item.state, item.zone].filter(Boolean).join(" | ") ||
              "Mapped pincode"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {CATEGORY_KEYS.map((key) => (
          <span key={key} style={chipStyle(CATEGORY_CONFIG[key].tone, item[key])}>
            {CATEGORY_CONFIG[key].label}
          </span>
        ))}
        {item.isNonServiceable && (
          <span style={chipStyle(CATEGORY_CONFIG.nonsvc.tone, true)}>
            {CATEGORY_CONFIG.nonsvc.label}
          </span>
        )}
      </div>
      <div style={popupMonoStyle}>
        {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
      </div>
    </div>
  );
};

const HubPopup = ({ item }) => (
  <div style={popupCardStyle}>
    <div style={{ ...popupHeaderStyle, marginBottom: 8 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          display: "grid",
          placeItems: "center",
          background: `${T.teal}15`,
          color: T.teal,
        }}
      >
        <HomeOutlined />
      </div>
      <div>
        <div style={{ fontWeight: 700, color: T.text }}>{item.premiseName}</div>
        <div style={popupMetaStyle}>
          {[item.city, item.state, item.zone].filter(Boolean).join(" | ") ||
            "Active hub"}
        </div>
      </div>
    </div>
    <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
      Pincode {item.pincode || "NA"}
    </div>
    <div style={popupMonoStyle}>
      {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
    </div>
  </div>
);

const MapControls = ({ onZoomIn, onZoomOut, onReset }) => (
  <div style={mapControlsWrapStyle}>
    {[
      { key: "zoom-in", label: "+", onClick: onZoomIn },
      { key: "zoom-out", label: "-", onClick: onZoomOut },
      { key: "reset", label: "R", onClick: onReset },
    ].map((control) => (
      <button
        key={control.key}
        type="button"
        onClick={control.onClick}
        style={mapControlStyle}
      >
        {control.label}
      </button>
    ))}
  </div>
);

const MapInfo = ({ items }) => (
  <div className="pinmap-map-info">
    {items.map(({ key, label, value, tone, Icon }) => (
      <span key={key} className="pinmap-map-info-item">
        <Icon style={{ color: toneColor[tone] }} /> {label} <b>{num(value)}</b>
      </span>
    ))}
  </div>
);

const AssistantMessage = ({ item, onFollowUp, onShowOnMap }) => {
  const hasFollowUps = Array.isArray(item.followUps) && item.followUps.length > 0;
  const hasMapPoints = Array.isArray(item.mapPoints) && item.mapPoints.length > 0;

  if (item.role === "user") {
    return (
      <div className="pinmap-chat-message pinmap-chat-message-user">
        <div className="pinmap-chat-avatar">
          {PINCODE_MAP_ASSISTANT_TEXT.userBubbleLabel}
        </div>
        <div className="pinmap-chat-bubble">{item.content}</div>
      </div>
    );
  }

  return (
    <div className="pinmap-chat-message pinmap-chat-message-assistant">
      <div className="pinmap-chat-avatar">
        <RobotOutlined />
      </div>
      <div className="pinmap-chat-bubble">
        <div className="pinmap-chat-answer">{item.content}</div>

        {hasMapPoints && (
          <button
            type="button"
            className="pinmap-chat-map-btn"
            onClick={() => onShowOnMap(item.mapPoints)}
          >
            {getShowOnMapLabel(item.mapPoints.length)}
          </button>
        )}

        {hasFollowUps && (
          <div className="pinmap-chat-followups">
            {item.followUps.slice(0, 3).map((followUp, index) => (
              <button
                type="button"
                key={`${item.id}-followup-${index}`}
                className="pinmap-chat-followup-btn"
                onClick={() => onFollowUp(followUp)}
              >
                {followUp}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const loadingStyle = {
  minHeight: "70vh",
  display: "grid",
  placeItems: "center",
  background: T.bg,
  color: T.muted,
};

const PINMAP_STYLES = `
  .pinmap-page{background:${T.bg};height:calc(100vh - 64px);overflow:hidden;font-family:Outfit,sans-serif;color:${T.text};display:flex;flex-direction:column}
  .ant-layout-content:has(.pinmap-page) + .ant-layout-footer,
  .ant-layout-content:has(.pinmap-page) + footer{display:none!important}
  .ant-layout-content:has(.pinmap-page){overflow:hidden!important;flex:1;min-height:0}
  .pinmap-topbar{display:flex;align-items:center;justify-content:space-between;padding:10px 28px;background:${T.white};border-bottom:1px solid ${T.border};z-index:950;gap:12px;flex-wrap:wrap;flex-shrink:0}
  .pinmap-topbar-left{display:flex;align-items:center;gap:12px}
  .pinmap-back-arrow{font-size:18px;cursor:pointer;color:${T.muted}}
  .pinmap-back-arrow:hover{color:${T.blue}}
  .pinmap-logo-icon{width:36px;height:36px;background:${T.blue};border-radius:10px;display:grid;place-items:center;color:#fff;font-size:17px}
  .pinmap-logo-text{font-size:17px;font-weight:700;letter-spacing:-.3px}
  .pinmap-logo-text span{color:${T.muted};font-weight:400}
  .pinmap-last-sync{display:flex;align-items:center;gap:6px;font-family:JetBrains Mono, monospace;font-size:11px;color:${T.soft}}
  .pinmap-sync-dot{width:6px;height:6px;border-radius:50%;background:${T.green};animation:pinmapDot 2s infinite}
  .pinmap-sync-dot.refreshing{animation-duration:.9s}
  .pinmap-alert-bar{display:flex;align-items:center;gap:10px;padding:8px 28px;background:#fffbeb;border-bottom:1px solid #fde68a;font-size:13px;color:#92400e;flex-wrap:wrap;z-index:940;flex-shrink:0}
  .pinmap-alert-count{font-family:JetBrains Mono, monospace;font-weight:600;background:rgba(234,179,8,.15);padding:2px 8px;border-radius:4px}
  .pinmap-alert-close{margin-left:auto;border:none;background:transparent;color:#92400e;font-size:16px;line-height:1;cursor:pointer;padding:0 2px}
  .pinmap-stats-panel{margin:12px 28px 0;background:${T.white};border:1px solid ${T.border};border-radius:14px;padding:${PINCODE_MAP_STYLE_TOKENS.STATS_PANEL_PADDING};flex-shrink:0}
  .pinmap-stats-head{display:flex;align-items:center;justify-content:space-between;gap:${PINCODE_MAP_STYLE_TOKENS.STATS_HEAD_GAP};margin-bottom:${PINCODE_MAP_STYLE_TOKENS.STATS_HEAD_MARGIN_BOTTOM}}
  .pinmap-stats-reset{border:none;background:transparent;color:${T.blue};font-size:${PINCODE_MAP_STYLE_TOKENS.STATS_RESET_FONT_SIZE};font-weight:600;cursor:pointer;text-decoration:underline;padding:0}
  .pinmap-stats-reset:hover{color:${PINCODE_MAP_STYLE_TOKENS.STATS_RESET_HOVER_COLOR}}
  .pinmap-stats-row{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:${PINCODE_MAP_STYLE_TOKENS.STATS_ROW_GAP}}
  .pinmap-stat-card{background:${T.white};border:1px solid ${T.border};border-radius:14px;padding:14px 16px;position:relative;overflow:hidden;cursor:pointer;transition:border-color .2s,box-shadow .2s;text-align:left}
  .pinmap-stat-card:hover{border-color:rgba(0,0,0,.1);box-shadow:0 4px 20px rgba(0,0,0,.04)}
  .pinmap-stat-card.pinmap-active{border-color:${T.blue};box-shadow:0 0 0 1px ${T.blue},0 4px 20px rgba(37,99,235,.08)}
  .pinmap-stat-label{font-size:10px;font-weight:500;color:${T.muted};text-transform:uppercase;letter-spacing:.7px;margin-bottom:6px;display:flex;align-items:center;gap:6px}
  .pinmap-stat-icon{width:26px;height:26px;border-radius:7px;display:grid;place-items:center;background:color-mix(in srgb, var(--pinmap-tone) 10%, white);color:var(--pinmap-tone);font-size:13px}
  .pinmap-stat-value{font-size:24px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-1px;line-height:1;color:var(--pinmap-tone)}
  .pinmap-stat-sub{font-size:10px;color:${T.soft};margin-top:4px;font-family:JetBrains Mono, monospace}
  .pinmap-stat-bar{position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--pinmap-tone),transparent)}
  .pinmap-main{display:grid;grid-template-columns:300px minmax(0,1fr) 380px;gap:0;flex:1;min-height:0;margin-top:10px;padding:0 28px 14px}
  .pinmap-main.pinmap-chat-collapsed{grid-template-columns:300px minmax(0,1fr)}
  .pinmap-sidebar{background:${T.white};border:1px solid ${T.border};border-radius:14px 0 0 14px;display:flex;flex-direction:column;overflow-y:auto;overflow-x:hidden;min-height:0}
  .pinmap-sidebar-section{padding:14px 18px;border-bottom:1px solid ${T.border};flex-shrink:0}
  .pinmap-section-title{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1.2px;color:${T.soft};margin-bottom:10px}
  .pinmap-search-box{display:flex;align-items:center;gap:10px;background:#f5f5f2;border:1px solid ${T.border};border-radius:10px;padding:9px 12px;color:${T.soft}}
  .pinmap-search-box input{border:none;background:none;outline:none;font-family:JetBrains Mono, monospace;font-size:13px;color:${T.text};width:100%}
  .pinmap-search-hint{margin-top:10px;padding:8px 10px;border-radius:8px;border:1px solid #bfdbfe;background:#eff6ff;display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:11px;color:#1e3a8a}
  .pinmap-search-hint button{border:none;background:transparent;color:${T.blue};cursor:pointer;font-size:11px;font-weight:600;padding:0;text-decoration:underline;white-space:nowrap}
  .pinmap-search-hint button:hover{color:#1d4ed8}
  .pinmap-filter-group{display:flex;flex-direction:column;gap:3px}
  .pinmap-filter-item{display:flex;align-items:center;gap:9px;padding:7px 10px;border-radius:7px;cursor:pointer;transition:background .15s;user-select:none;border:none;background:transparent;text-align:left;width:100%;color:${T.text}}
  .pinmap-filter-item:hover{background:#f5f5f2}
  .pinmap-filter-check{width:16px;height:16px;border-radius:4px;border:2px solid rgba(0,0,0,.1);display:grid;place-items:center;transition:all .15s;flex-shrink:0;font-size:10px;font-weight:700;color:#fff}
  .pinmap-filter-check.checked{border-color:transparent;background:var(--pinmap-tone)}
  .pinmap-filter-icon{display:inline-flex;align-items:center;justify-content:center;color:${T.muted}}
  .pinmap-filter-label{font-size:12px;font-weight:500;flex:1}
  .pinmap-filter-count{font-family:JetBrains Mono, monospace;font-size:11px;color:${T.soft};font-weight:500}
  .pinmap-legend-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px}
  .pinmap-legend-item{display:flex;align-items:center;gap:5px;font-size:11px;color:${T.muted}}
  .pinmap-legend-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .pinmap-results-header{padding:12px 18px;border-bottom:1px solid ${T.border};display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:${T.white};z-index:2;flex-shrink:0}
  .pinmap-results-title{margin:0}
  .pinmap-results-count{font-family:JetBrains Mono, monospace;font-size:11px;color:${T.muted}}
  .pinmap-results-count b{color:${T.blue}}
  .pinmap-results-section{flex:1 1 220px;overflow-y:auto;min-height:220px}
  .pinmap-results-section::-webkit-scrollbar{width:3px}
  .pinmap-results-section::-webkit-scrollbar-thumb{background:rgba(0,0,0,.1);border-radius:2px}
  .pinmap-result-row{display:flex;align-items:center;gap:10px;padding:8px 18px;border:none;border-bottom:1px solid rgba(0,0,0,.03);cursor:pointer;transition:background .15s;background:transparent;text-align:left;width:100%}
  .pinmap-result-row:hover{background:#f5f5f2}
  .pinmap-result-pin{font-family:JetBrains Mono, monospace;font-size:12px;font-weight:600;color:${T.text};min-width:55px}
  .pinmap-result-city{font-size:11px;color:${T.muted};flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pinmap-result-tags{display:flex;gap:3px}
  .pinmap-result-tag{width:7px;height:7px;border-radius:50%}
  .pinmap-result-hub{font-size:9px;font-weight:600;color:${T.teal};background:rgba(13,148,136,.08);padding:1px 5px;border-radius:3px}
  .pinmap-result-nonsvc{font-size:9px;font-weight:600;color:${T.red};background:rgba(220,38,38,.06);padding:1px 5px;border-radius:3px}
  .pinmap-empty{padding:16px;font-size:12px;color:${T.soft}}
  .pinmap-map-wrap{position:relative;background:${T.white};border-top:1px solid ${T.border};border-bottom:1px solid ${T.border};overflow:hidden;min-height:0;height:100%}
  .pinmap-chat-expand-btn{
    position:absolute;top:14px;left:14px;z-index:820;border:1px solid ${T.border};background:${T.white};color:${T.text};
    border-radius:10px;padding:7px 12px;font-size:11px;font-weight:600;display:flex;align-items:center;gap:6px;cursor:pointer;
    box-shadow:0 4px 12px rgba(0,0,0,.08);transition:all .2s
  }
  .pinmap-chat-expand-btn:hover{background:#eff6ff;border-color:#bfdbfe;color:${T.blue}}
  .pinmap-chat-pane{display:flex;flex-direction:column;background:${T.white};border:1px solid ${T.border};border-left:none;border-radius:0 14px 14px 0;overflow:hidden;min-height:0;height:100%;overscroll-behavior:contain}
  .pinmap-main.pinmap-chat-collapsed .pinmap-chat-pane{display:none}
  .pinmap-chat-header{padding:12px 14px;border-bottom:1px solid ${T.border};display:flex;align-items:center;justify-content:space-between;gap:8px;flex-shrink:0;background:linear-gradient(135deg,#f8f9ff 0%,${T.white} 100%)}
  .pinmap-chat-title-wrap{display:flex;align-items:center;gap:10px}
  .pinmap-chat-header-actions{display:flex;align-items:center;gap:8px}
  .pinmap-chat-collapse-btn{width:28px;height:28px;border:1px solid ${T.border};background:${T.white};border-radius:7px;color:${T.muted};display:grid;place-items:center;cursor:pointer;transition:all .15s}
  .pinmap-chat-collapse-btn:hover{color:${T.blue};border-color:#bfdbfe;background:#eff6ff}
  .pinmap-chat-badge{width:28px;height:28px;border-radius:8px;background:#6366f1;display:grid;place-items:center;color:#fff;font-size:14px}
  .pinmap-chat-title{font-size:13px;font-weight:700;color:${T.text}}
  .pinmap-chat-subtitle{font-size:10px;color:${T.soft}}
  .pinmap-chat-messages{flex:1;overflow-y:auto;padding:12px 14px;display:flex;flex-direction:column;gap:10px;background:#fcfcfb;overscroll-behavior:contain;scrollbar-gutter:stable}
  .pinmap-chat-messages::-webkit-scrollbar{width:4px}
  .pinmap-chat-messages::-webkit-scrollbar-thumb{background:rgba(0,0,0,.12);border-radius:4px}
  .pinmap-chat-message{display:flex;gap:8px;align-items:flex-start}
  .pinmap-chat-message-user{justify-content:flex-end}
  .pinmap-chat-message-user .pinmap-chat-avatar{order:2;background:#e5e7eb;color:${T.muted}}
  .pinmap-chat-message-user .pinmap-chat-bubble{order:1;background:#6366f1;color:#fff;border-bottom-right-radius:5px}
  .pinmap-chat-message-assistant .pinmap-chat-bubble{background:#f5f5f2;color:${T.text};border-bottom-left-radius:5px}
  .pinmap-chat-avatar{width:24px;height:24px;border-radius:7px;display:grid;place-items:center;flex-shrink:0;font-size:10px;font-weight:700;background:#6366f1;color:#fff}
  .pinmap-chat-bubble{max-width:100%;border-radius:12px;padding:9px 12px;font-size:12px;line-height:1.55}
  .pinmap-chat-answer{white-space:pre-wrap;word-break:break-word}
  .pinmap-chat-followups{margin-top:8px;display:flex;flex-wrap:wrap;gap:6px}
  .pinmap-chat-followup-btn{border:1px solid #c7d2fe;background:#eef2ff;color:#3730a3;font-size:10px;border-radius:999px;padding:4px 10px;cursor:pointer;transition:all .15s}
  .pinmap-chat-followup-btn:hover{background:#e0e7ff;border-color:#a5b4fc}
  .pinmap-chat-map-btn{margin-top:8px;border:none;background:#eef2ff;color:#3730a3;font-size:11px;border-radius:8px;padding:5px 10px;cursor:pointer;font-weight:600;transition:background .15s}
  .pinmap-chat-map-btn:hover{background:#e0e7ff}
  .pinmap-chat-welcome{text-align:center;padding:6px 8px 12px}
  .pinmap-chat-welcome-icon{width:44px;height:44px;background:rgba(99,102,241,.08);border-radius:12px;display:grid;place-items:center;margin:0 auto 10px;color:#6366f1;font-size:20px}
  .pinmap-chat-welcome-title{font-size:14px;font-weight:700;margin-bottom:4px}
  .pinmap-chat-welcome-desc{font-size:11px;color:${T.muted};line-height:1.5;max-width:300px;margin:0 auto 12px}
  .pinmap-chat-suggestions{display:flex;flex-direction:column;gap:5px}
  .pinmap-chat-suggestion-btn{
    border:1px solid ${T.border};background:#f9fafb;color:${T.text};border-radius:9px;padding:7px 10px;
    text-align:left;font-size:11px;cursor:pointer;line-height:1.4;display:flex;align-items:center;gap:8px;transition:all .15s
  }
  .pinmap-chat-suggestion-btn:hover{border-color:#c7d2fe;background:#eef2ff}
  .pinmap-chat-sug-num{
    width:20px;height:20px;border-radius:6px;display:grid;place-items:center;
    flex-shrink:0;font-size:10px;font-weight:600;background:rgba(99,102,241,.08);color:#6366f1
  }
  .pinmap-chat-typing{display:flex;gap:4px}
  .pinmap-chat-typing span{width:6px;height:6px;border-radius:50%;background:${T.soft};animation:pinmapTyping 1.3s infinite}
  .pinmap-chat-typing span:nth-child(2){animation-delay:.2s}
  .pinmap-chat-typing span:nth-child(3){animation-delay:.4s}
  .pinmap-chat-error{margin:0 14px 8px 14px;border:1px solid #fecaca;background:#fef2f2;color:#b91c1c;border-radius:10px;padding:7px 10px;font-size:11px;flex-shrink:0}
  .pinmap-chat-input-wrap{padding:8px 12px;border-top:1px solid ${T.border};background:${T.white};flex-shrink:0}
  .pinmap-chat-input{display:flex;align-items:center;gap:8px;background:#f5f5f2;border:1px solid ${T.border};border-radius:11px;padding:7px 10px}
  .pinmap-chat-input input{border:none;background:none;outline:none;width:100%;font-size:12px;color:${T.text};font-family:inherit}
  .pinmap-chat-send{width:30px;height:30px;border:none;border-radius:8px;background:#6366f1;display:grid;place-items:center;color:#fff;cursor:pointer;opacity:.45;transition:opacity .15s}
  .pinmap-chat-send.active{opacity:1}
  .pinmap-chat-send:disabled{cursor:not-allowed}
  .pinmap-assistant-focus{width:16px;height:16px;border-radius:50%;background:#fff;border:3px solid #4f46e5;box-shadow:0 0 0 4px rgba(79,70,229,.2),0 2px 8px rgba(0,0,0,.2)}
  .pinmap-map-info{
    position:absolute;left:14px;bottom:14px;z-index:800;display:flex;flex-wrap:wrap;gap:14px;background:${T.white};
    border:1px solid ${T.border};border-radius:10px;padding:10px 14px;box-shadow:0 4px 16px rgba(0,0,0,.08);font-size:11px;color:${T.muted}
  }
  .pinmap-map-info-item{display:flex;align-items:center;gap:5px}
  .pinmap-map-info-item b{font-family:JetBrains Mono, monospace;color:${T.text}}
  .leaflet-popup-content-wrapper{background:transparent!important;box-shadow:none!important;padding:0!important}
  .leaflet-popup-content{margin:0!important}
  .leaflet-popup-tip-container,.leaflet-control-zoom{display:none!important}
  .leaflet-control-attribution{font-size:10px!important;background:rgba(255,255,255,.86)!important}
  .pinmap-pin{width:11px;height:11px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)}
  .pinmap-pin.courier{background:${T.blue}}
  .pinmap-pin.ecomm{background:${T.green}}
  .pinmap-pin.mcn{background:${T.orange}}
  .pinmap-pin.multi{background:${T.purple}}
  .pinmap-pin.nonsvc{background:${T.red}}
  .pinmap-hub{width:32px;height:32px;position:relative;display:flex;align-items:center;justify-content:center}
  .pinmap-hub-pulse{position:absolute;width:36px;height:36px;border-radius:50%;background:rgba(13,148,136,.12);animation:pinmapPulse 2.5s ease-out infinite;z-index:1}
  .pinmap-hub-core{width:28px;height:28px;background:#fff;border:2px solid ${T.teal};border-radius:7px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(13,148,136,.35);position:relative;z-index:2}
  .pinmap-cluster{display:flex;align-items:center;justify-content:center;border-radius:50%;color:#fff;font-weight:700;font-family:JetBrains Mono, monospace;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.2);background:rgba(37,99,235,.9)}
  .pinmap-cluster.nonsvc{background:rgba(220,38,38,.8)}
  @keyframes pinmapDot{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes pinmapPulse{0%{transform:scale(.6);opacity:.7}100%{transform:scale(2);opacity:0}}
  @keyframes pinmapTyping{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}
  @media (max-width: 1360px){
    .pinmap-stats-row{grid-template-columns:repeat(3,minmax(0,1fr))}
    .pinmap-main{grid-template-columns:280px minmax(0,1fr)}
    .pinmap-main.pinmap-chat-collapsed{grid-template-columns:280px minmax(0,1fr)}
    .pinmap-chat-pane{grid-column:1/-1;border-left:1px solid ${T.border};border-top:none;border-radius:0 0 14px 14px;min-height:360px}
    .pinmap-sidebar{border-radius:14px 0 0 0}
  }
  @media (max-width: 1100px){
    .pinmap-page{height:auto;overflow:visible}
    .pinmap-main{grid-template-columns:1fr;flex:none;min-height:initial;padding:0 16px 16px}
    .pinmap-main.pinmap-chat-collapsed{grid-template-columns:1fr}
    .pinmap-sidebar{border-radius:14px}
    .pinmap-map-wrap{border:1px solid ${T.border};border-radius:14px;margin-top:14px;min-height:520px}
    .pinmap-chat-pane{border:1px solid ${T.border};border-radius:14px;margin-top:14px;min-height:480px}
    .pinmap-main.pinmap-chat-collapsed .pinmap-chat-pane{display:none}
  }
  @media (max-width: 600px){
    .pinmap-topbar,.pinmap-alert-bar{padding-left:16px;padding-right:16px}
    .pinmap-stats-panel{margin:${PINCODE_MAP_STYLE_TOKENS.MOBILE_STATS_PANEL_MARGIN};padding:${PINCODE_MAP_STYLE_TOKENS.MOBILE_STATS_PANEL_PADDING}}
    .pinmap-stats-row{grid-template-columns:repeat(2,minmax(0,1fr));gap:${PINCODE_MAP_STYLE_TOKENS.MOBILE_STATS_ROW_GAP}}
    .pinmap-main{padding:0 16px 16px}
    .pinmap-stat-value{font-size:22px}
    .pinmap-map-info{right:14px}
    .pinmap-stats-head{flex-wrap:wrap}
  }
`;

const PincodeMap = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pincodeQueryParam =
    searchParams.get("pincode");
  const queryPincode = useMemo(
    () => normalizeSearchToken(decodeParam(pincodeQueryParam)),
    [pincodeQueryParam]
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pincodes, setPincodes] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [assistantHighlights, setAssistantHighlights] = useState([]);
  const [showMissingWarning, setShowMissingWarning] = useState(true);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPending, setChatPending] = useState(false);
  const [chatError, setChatError] = useState("");
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);

  const [meta, setMeta] = useState({
    pincodeTotal: 0,
    pincodeMapped: 0,
    pincodeMissing: 0,
    hubTotal: 0,
    hubMapped: 0,
  });
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  const chatBodyRef = useRef(null);
  const markerRefs = useRef({ pincode: new Map(), hub: new Map() });
  const assistantMarkerRefs = useRef(new Map());
  const lastAutoFocusedPincodeIdRef = useRef("");

  const markerRef = useCallback(
    (type, id) => (marker) => {
      if (marker) markerRefs.current[type].set(id, marker);
      else markerRefs.current[type].delete(id);
    },
    []
  );

  const resetMapView = useCallback(() => {
    mapRef.current?.setView(INDIA_CENTER, DEFAULT_ZOOM, { animate: true });
  }, []);

  const openPopupWhenReady = useCallback((type, id) => {
    let attempts = 0;

    const tryOpen = () => {
      const marker = markerRefs.current[type].get(id);
      if (marker) {
        marker.openPopup();
        return;
      }
      if (attempts >= POPUP_OPEN_MAX_RETRIES) return;

      attempts += 1;
      window.setTimeout(tryOpen, POPUP_OPEN_RETRY_DELAY_MS);
    };

    tryOpen();
  }, []);

  const assistantMarkerRef = useCallback(
    (id) => (marker) => {
      if (marker) assistantMarkerRefs.current.set(id, marker);
      else assistantMarkerRefs.current.delete(id);
    },
    []
  );

  const openAssistantPopupWhenReady = useCallback((id) => {
    let attempts = 0;

    const tryOpen = () => {
      const marker = assistantMarkerRefs.current.get(id);
      if (marker) {
        marker.openPopup();
        return;
      }
      if (attempts >= POPUP_OPEN_MAX_RETRIES) return;

      attempts += 1;
      window.setTimeout(tryOpen, POPUP_OPEN_RETRY_DELAY_MS);
    };

    tryOpen();
  }, []);

  const focus = useCallback(
    (item, type) => {
      if (!mapRef.current) return;

      const zoom = type === "hub" ? 9 : PINCODE_FOCUS_ZOOM;
      const openPopup = () => openPopupWhenReady(type, item.id);

      mapRef.current.once("moveend", openPopup);
      mapRef.current.setView([item.lat, item.lng], zoom, {
        animate: true,
      });
      window.setTimeout(openPopup, 250);
    },
    [openPopupWhenReady]
  );

  const loadData = useCallback(async () => {
    setRefreshing(true);

    const [allPinsResult, hubsResult] = await Promise.allSettled([
      getAllNonServiceablePincodes(),
      getHubMapListing(),
    ]);

    let mappedPincodes = [];
    let mappedHubs = [];
    let allPincodes = [];
    let allHubs = [];

    if (allPinsResult.status === FULFILLED) {
      const allPins = (allPinsResult.value?.data || []).map(mapPincode);
      const serviceablePins = allPins.filter((item) => !item.isNonServiceable);

      allPincodes = mergePincodeDatasets(serviceablePins, allPins);
      mappedPincodes = allPincodes.filter(
        (item) => item.hasCoordinates && item.pincode
      );
      setPincodes(mappedPincodes);
    } else {
      setPincodes([]);
      message.error(PINCODE_MAP_MESSAGES.PINCODE_SERVICEABILITY_LOAD_FAILED);
    }

    if (hubsResult.status === FULFILLED) {
      allHubs = (hubsResult.value?.data || []).map(mapHub);
      mappedHubs = allHubs.filter(
        (item) => item.hasCoordinates && item.premiseName
      );
      setHubs(mappedHubs);
    } else {
      setHubs([]);
      message.error(PINCODE_MAP_MESSAGES.HUB_LIST_LOAD_FAILED);
    }

    setMeta(buildMeta(allPincodes, mappedPincodes, allHubs, mappedHubs));
    setLastRefreshed(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSearch(queryPincode);
  }, [queryPincode]);

  useEffect(() => {
    if (meta.pincodeMissing > 0) {
      setShowMissingWarning(true);
    }
  }, [meta.pincodeMissing]);

  const stats = useMemo(() => buildStats(pincodes, hubs), [hubs, pincodes]);
  const statCards = useMemo(() => buildStatCards(stats), [stats]);
  const query = normalizeSearchToken(search);
  const isDefaultFilterState = useMemo(
    () =>
      Object.keys(DEFAULT_FILTERS).every(
        (key) => Boolean(filters[key]) === Boolean(DEFAULT_FILTERS[key])
      ),
    [filters]
  );
  const isNumericQuery = useMemo(
    () => NUMERIC_QUERY_PATTERN.test(query),
    [query]
  );
  const pincodeMatchesForNumericQuery = useMemo(
    () =>
      isNumericQuery
        ? pincodes.filter((item) =>
          !item.isNonServiceable && matchesQuery([item.pincode], query)
        )
        : [],
    [isNumericQuery, pincodes, query]
  );
  const visiblePincodes = useMemo(
    () =>
      pincodes.filter(
        (item) =>
          matchesFilters(item, filters) &&
          matchesQuery([item.pincode, item.city, item.state, item.zone], query)
      ),
    [filters, pincodes, query]
  );

  const visibleHubs = useMemo(
    () =>
      shouldShowHubs(filters)
        ? hubs.filter((item) => {
          const fields = [
            item.premiseName,
            item.city,
            item.state,
            item.zone,
            item.pincode,
          ];
          return matchesQuery(fields, query);
        })
        : [],
    [filters, hubs, query]
  );

  const visibleStats = useMemo(
    () => ({
      ...buildPincodeStats(visiblePincodes),
      hubs: visibleHubs.length,
    }),
    [visibleHubs.length, visiblePincodes]
  );

  const mapInfoItems = useMemo(
    () => buildMapInfoItems(visibleStats),
    [visibleStats]
  );

  const results = useMemo(() => {
    const pincodeResults = visiblePincodes.map((item) => ({
      ...item,
      resultType: "pincode",
    }));
    const hubResults = query
      ? visibleHubs.map((item) => ({
        ...item,
        resultType: "hub",
      }))
      : [];

    return [...pincodeResults, ...hubResults].slice(0, RESULT_LIMIT);
  }, [query, visibleHubs, visiblePincodes]);

  const pincodeByCode = useMemo(() => {
    const lookup = new Map();
    pincodes.forEach((item) => {
      if (!item?.pincode) return;
      lookup.set(item.pincode, item);
    });
    return lookup;
  }, [pincodes]);

  const selectedPincodeFromContext = useMemo(() => {
    if (!EXACT_PINCODE_PATTERN.test(query)) return undefined;
    const matched = pincodes.find(
      (item) => normalizeSearchToken(item.pincode) === query
    );
    return matched?.pincode || query;
  }, [pincodes, query]);

  const assistantContext = useMemo(
    () => ({
      page: "pincode-map",
      selected_pincode: selectedPincodeFromContext,
      search_query: txt(search) || undefined,
      visible_filters: Object.keys(filters).filter((key) => filters[key]),
    }),
    [filters, search, selectedPincodeFromContext]
  );

  const showAssistantMapPoints = useCallback(
    (points) => {
      if (!Array.isArray(points) || !points.length) {
        setAssistantHighlights([]);
        message.info(PINCODE_MAP_ASSISTANT_TEXT.mapNoLocations);
        return;
      }

      const normalizedPoints = points.filter(
        (point) =>
          point &&
          Number.isFinite(point.lat) &&
          Number.isFinite(point.lng) &&
          txt(point.id)
      );
      if (!normalizedPoints.length) {
        setAssistantHighlights([]);
        message.info(PINCODE_MAP_ASSISTANT_TEXT.mapNoLocations);
        return;
      }

      setAssistantHighlights(normalizedPoints);

      if (!mapRef.current) return;

      const firstPoint = normalizedPoints[0];
      const openPrimaryPopup = () => openAssistantPopupWhenReady(firstPoint.id);

      mapRef.current.once("moveend", openPrimaryPopup);
      window.setTimeout(openPrimaryPopup, 260);

      if (normalizedPoints.length === 1) {
        mapRef.current.setView([firstPoint.lat, firstPoint.lng], PINCODE_FOCUS_ZOOM + 1, {
          animate: true,
        });
        return;
      }

      const bounds = L.latLngBounds(
        normalizedPoints.map((point) => [point.lat, point.lng])
      );
      mapRef.current.fitBounds(bounds, {
        padding: [70, 70],
        maxZoom: PINCODE_FOCUS_ZOOM,
        animate: true,
      });
    },
    [openAssistantPopupWhenReady]
  );

  const submitAssistantQuery = useCallback(
    async (rawQuestion) => {
      const question = txt(rawQuestion);
      if (!question || chatPending) return;

      setChatError("");
      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: question,
      };

      const conversation = buildAssistantConversation(chatMessages);
      setChatMessages((prev) => [...prev, userMessage].slice(-CHAT_HISTORY_LIMIT));
      setChatPending(true);

      try {
        const response = await askPincodeMapAssistant({
          question,
          conversation,
          context: assistantContext,
          options: { debug: false },
        });

        const resolvedEntities =
          response?.resolved_entities &&
            typeof response.resolved_entities === "object"
            ? response.resolved_entities
            : {};

        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content:
            sanitizeAssistantAnswer(response?.answer) ||
            PINCODE_MAP_ASSISTANT_TEXT.noResponseGenerated,
          followUps: buildActionableFollowUps({
            followUps: Array.isArray(response?.follow_ups) ? response.follow_ups : [],
            resolvedEntities,
            selectedPincode: assistantContext.selected_pincode,
            currentQuestion: question,
          }),
          mapPoints: resolveMapPointsFromEntities(
            resolvedEntities,
            pincodeByCode,
            hubs,
            {
              answerText: response?.answer,
              questionText: question,
              citations: Array.isArray(response?.citations) ? response.citations : [],
            }
          ),
        };

        setChatMessages((prev) => [...prev, assistantMessage].slice(-CHAT_HISTORY_LIMIT));
      } catch (error) {
        setChatError(
          error?.message || PINCODE_MAP_ASSISTANT_TEXT.requestFailed
        );
      } finally {
        setChatPending(false);
      }
    },
    [assistantContext, chatMessages, chatPending, hubs, pincodeByCode]
  );

  const handleChatSend = useCallback(() => {
    if (!txt(chatInput)) return;
    submitAssistantQuery(chatInput);
    setChatInput("");
  }, [chatInput, submitAssistantQuery]);

  useEffect(() => {
    if (!chatBodyRef.current) return;
    chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chatMessages, chatPending]);

  useEffect(() => {
    if (!EXACT_PINCODE_PATTERN.test(query) || !mapRef.current || !isMapReady) {
      lastAutoFocusedPincodeIdRef.current = "";
      return;
    }

    const exactPin = visiblePincodes.find(
      (item) => normalizeSearchToken(item.pincode) === query
    );
    if (!exactPin) return;
    if (lastAutoFocusedPincodeIdRef.current === exactPin.id) return;

    lastAutoFocusedPincodeIdRef.current = exactPin.id;
    focus(exactPin, "pincode");
  }, [focus, isMapReady, query, visiblePincodes]);

  const handleCardSelect = useCallback(
    (key) => {
      if (key === PINCODE_MAP_FILTER_CARD_KEYS.ALL) {
        setFilters(DEFAULT_FILTERS);
        resetMapView();
        return;
      }

      setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [resetMapView]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const commitSearchToQueryParam = useCallback(
    (value) => {
      const nextParams = new URLSearchParams(searchParams);
      const nextPincode = txt(value);

      if (nextPincode) {
        nextParams.set("pincode", nextPincode);
      } else {
        nextParams.delete("pincode");
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const queryPincodeHiddenByFilters = useMemo(
    () => {
      if (!pincodeMatchesForNumericQuery.length) return false;

      const visibleIds = new Set(visiblePincodes.map((item) => item.id));
      return pincodeMatchesForNumericQuery.some(
        (item) => !visibleIds.has(item.id)
      );
    },
    [pincodeMatchesForNumericQuery, visiblePincodes]
  );
  const hiddenPincodeCount = useMemo(
    () => {
      if (!pincodeMatchesForNumericQuery.length) return 0;

      const visibleIds = new Set(visiblePincodes.map((item) => item.id));
      return pincodeMatchesForNumericQuery.reduce(
        (count, item) => (visibleIds.has(item.id) ? count : count + 1),
        0
      );
    },
    [pincodeMatchesForNumericQuery, visiblePincodes]
  );

  if (loading) {
    return <div style={loadingStyle}>Loading pincode coverage dashboard...</div>;
  }

  return (
    <React.Fragment>
      <div className="pinmap-page">
        <div className="pinmap-topbar">
          <div className="pinmap-topbar-left">
            <ArrowLeftOutlined
              className="pinmap-back-arrow"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            />
            <div className="pinmap-logo-icon">
              <EnvironmentOutlined />
            </div>
            <div className="pinmap-logo-text">
              Pincode Coverage <span>/ Intelligence</span>
            </div>
          </div>
          <div className="pinmap-last-sync">
            <span
              className={`pinmap-sync-dot ${refreshing ? "refreshing" : ""}`}
            ></span>
            Last synced {time(lastRefreshed)}
          </div>
        </div>

        {showMissingWarning && meta.pincodeMissing > 0 && (
          <div className="pinmap-alert-bar">
            <WarningOutlined />
            <span className="pinmap-alert-count">{num(meta.pincodeMissing)}</span>
            <span>
              pincodes missing lat/lng coordinates {"\u2014"} not rendered on map
            </span>
            <button
              type="button"
              className="pinmap-alert-close"
              aria-label="Close missing pincodes warning"
              onClick={() => setShowMissingWarning(false)}
            >
              x
            </button>
          </div>
        )}

        <div className="pinmap-stats-panel">
          <div className="pinmap-stats-head">
            <div
              className="pinmap-section-title"
              style={{
                marginBottom:
                  PINCODE_MAP_STYLE_TOKENS.STATS_SECTION_TITLE_MARGIN_BOTTOM,
              }}
            >
              {PINCODE_MAP_FILTER_PANEL_TEXT.TITLE}
            </div>
            <button type="button" className="pinmap-stats-reset" onClick={clearFilters}>
              {PINCODE_MAP_FILTER_PANEL_TEXT.RESET_BUTTON}
            </button>
          </div>
          <div className="pinmap-stats-row">
            {statCards.map((item) => (
              <StatCard
                key={item.key}
                item={item}
                active={
                  item.key === PINCODE_MAP_FILTER_CARD_KEYS.ALL
                    ? isDefaultFilterState
                    : filters[item.key]
                }
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </div>

        <div
          className={`pinmap-main ${isChatCollapsed ? "pinmap-chat-collapsed" : ""}`}
        >
          <div className="pinmap-sidebar">
            <div className="pinmap-sidebar-section">
              <div className="pinmap-section-title">Search</div>
              <div className="pinmap-search-box">
                <SearchOutlined />
                <input
                  value={search}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setSearch(nextValue);
                    commitSearchToQueryParam(nextValue);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitSearchToQueryParam(event.currentTarget.value);
                    }
                  }}
                  placeholder="Search pincode, city, hub..."
                />
              </div>
              {queryPincodeHiddenByFilters && (
                <div className="pinmap-search-hint">
                  <span>
                    {num(hiddenPincodeCount)} matching pincode
                    {hiddenPincodeCount > 1 ? "s are" : " is"} hidden by
                    current filters.
                  </span>
                  <button type="button" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              )}
            </div>

            <div className="pinmap-sidebar-section">
              <div className="pinmap-section-title">Legend</div>
              <Legend />
            </div>

            <div className="pinmap-results-header">
              <div className="pinmap-section-title pinmap-results-title">
                Results
              </div>
              <div className="pinmap-results-count">
                Showing <b>{num(results.length)}</b>
              </div>
            </div>

            <div className="pinmap-results-section">
              {results.length ? (
                results.map((item) => (
                  <ResultRow key={`${item.resultType}-${item.id}`} item={item} onFocus={focus} />
                ))
              ) : (
                <div className="pinmap-empty">
                  No records match the current filters.
                </div>
              )}
            </div>
          </div>

          <div className="pinmap-map-wrap">
            <MapContainer
              center={INDIA_CENTER}
              zoom={DEFAULT_ZOOM}
              ref={mapRef}
              whenReady={() => setIsMapReady(true)}
              style={{ height: "100%", minHeight: 620, width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution={
                  "&copy; <a href=\"https://www.openstreetmap.org/\">OpenStreetMap</a> contributors &copy; CARTO"
                }
                url={TILE_URL}
              />
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                zoomToBoundsOnClick
                maxClusterRadius={40}
                disableClusteringAtZoom={10}
                iconCreateFunction={clusterIcon}
              >
                {visiblePincodes.map((item) => {
                  const tone = getPincodeTone(item);

                  return (
                    <Marker
                      key={item.id}
                      position={[item.lat, item.lng]}
                      icon={pinIcon(tone)}
                      markerTone={tone}
                      ref={markerRef("pincode", item.id)}
                    >
                      <Popup closeButton={false} maxWidth={260}>
                        <PincodePopup item={item} />
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>

              {visibleHubs.map((item) => (
                <Marker
                  key={item.id}
                  position={[item.lat, item.lng]}
                  icon={hubIcon}
                  ref={markerRef("hub", item.id)}
                >
                  <Popup closeButton={false} maxWidth={260}>
                    <HubPopup item={item} />
                  </Popup>
                </Marker>
              ))}

              {assistantHighlights.map((item) => (
                <Marker
                  key={item.id}
                  position={[item.lat, item.lng]}
                  icon={assistantFocusIcon}
                  zIndexOffset={2200}
                  ref={assistantMarkerRef(item.id)}
                >
                  <Popup closeButton={false} maxWidth={260}>
                    {item.pointType === "hub" && item.popupItem ? (
                      <HubPopup item={item.popupItem} />
                    ) : item.popupItem ? (
                      <PincodePopup item={item.popupItem} />
                    ) : (
                      <div style={popupCardStyle}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.label}</div>
                        <div style={popupMonoStyle}>
                          {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
                        </div>
                      </div>
                    )}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            <MapControls
              onZoomIn={() => mapRef.current?.zoomIn()}
              onZoomOut={() => mapRef.current?.zoomOut()}
              onReset={resetMapView}
            />

            {isChatCollapsed && (
              <button
                type="button"
                className="pinmap-chat-expand-btn"
                onClick={() => setIsChatCollapsed(false)}
              >
                <DoubleLeftOutlined />
                {PINCODE_MAP_ASSISTANT_TEXT.openAssistant}
              </button>
            )}

            <MapInfo items={mapInfoItems} />
          </div>

          <div className="pinmap-chat-pane">
            <div className="pinmap-chat-header">
              <div className="pinmap-chat-title-wrap">
                <div className="pinmap-chat-badge">
                  <RobotOutlined />
                </div>
                <div>
                  <div className="pinmap-chat-title">
                    {PINCODE_MAP_ASSISTANT_TEXT.title}
                  </div>
                  <div className="pinmap-chat-subtitle">
                    {PINCODE_MAP_ASSISTANT_TEXT.subtitle}
                  </div>
                </div>
              </div>
              <div className="pinmap-chat-header-actions">
                <button
                  type="button"
                  className="pinmap-chat-collapse-btn"
                  onClick={() => setIsChatCollapsed(true)}
                  aria-label="Collapse assistant panel"
                  title="Collapse assistant panel"
                >
                  <DoubleRightOutlined />
                </button>
              </div>
            </div>

            <div className="pinmap-chat-messages" ref={chatBodyRef}>
              {!chatMessages.length && !chatPending && (
                <div className="pinmap-chat-welcome">
                  <div className="pinmap-chat-welcome-icon">
                    <RobotOutlined />
                  </div>
                  <div className="pinmap-chat-welcome-title">
                    {PINCODE_MAP_ASSISTANT_TEXT.welcomeTitle}
                  </div>
                  <div className="pinmap-chat-welcome-desc">
                    {PINCODE_MAP_ASSISTANT_TEXT.welcomeDescription}
                  </div>
                  <div className="pinmap-chat-suggestions">
                    {ASSISTANT_SUGGESTIONS.map((suggestion, index) => (
                      <button
                        type="button"
                        key={suggestion}
                        className="pinmap-chat-suggestion-btn"
                        onClick={() => submitAssistantQuery(suggestion)}
                      >
                        <span className="pinmap-chat-sug-num">{index + 1}</span>
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {chatMessages.map((item) => (
                <AssistantMessage
                  key={item.id}
                  item={item}
                  onFollowUp={submitAssistantQuery}
                  onShowOnMap={showAssistantMapPoints}
                />
              ))}

              {chatPending && (
                <div className="pinmap-chat-message pinmap-chat-message-assistant">
                  <div className="pinmap-chat-avatar">
                    <RobotOutlined />
                  </div>
                  <div className="pinmap-chat-bubble">
                    <div className="pinmap-chat-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {chatError && (
              <div className="pinmap-chat-error">
                <InfoCircleOutlined style={{ marginRight: 6 }} />
                {chatError}
              </div>
            )}

            <div className="pinmap-chat-input-wrap">
              <div className="pinmap-chat-input">
                <input
                  value={chatInput}
                  onChange={(event) => setChatInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleChatSend();
                    }
                  }}
                  placeholder={PINCODE_MAP_ASSISTANT_TEXT.inputPlaceholder}
                />
                <button
                  type="button"
                  className={`pinmap-chat-send ${
                    txt(chatInput) && !chatPending ? "active" : ""
                  }`}
                  disabled={!txt(chatInput) || chatPending}
                  onClick={handleChatSend}
                >
                  <SendOutlined />
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{PINMAP_STYLES}</style>
      </div>
    </React.Fragment>
  );
};

export default PincodeMap;
